/**
 * MCP RPC client — single transport module the console uses to talk to Kuvio.
 *
 * Wraps JSON-RPC over POST. Handles batch requests, surfaces typed entity
 * operations, and maps MCP errors into MutationError shapes for the UI.
 *
 * Auth: Uses Bearer token when authenticated.
 */

import { getAccessToken, getCurrentWorkspaceId } from "./auth";
import { getCurrentScope } from "../v3/hooks/use-session-scope";
import type { NeedsYouFeed, AnsweredDecision, NeedsYouResumeResult } from "./types";
import type { HandledDay } from "../v3/routes/app/handled-model";
import type {
  AgentConfig,
  ArchiveResult,
  CascadeArchiveResult,
  EntityRecord,
  EntityTypeDefinition,
  FileMetadata,
  GetFileResult,
  JsonRpcRequest,
  JsonRpcResponse,
  LibraryNode,
  LibraryNodeDetail,
  FindRelatedResult,
  ListActivityResult,
  ListChildrenResult,
  ListEntitiesResult,
  ListFilesResult,
  ListFilters,
  McpToolResult,
  MutationError,
  Task,
  TaskStatus,
  TaskReference,
  TaskReferenceKind,
  TaskDetailResult,
  AgentBrainSnapshot,
  RecentEvent,
  PromptArgDef,
  RowDetailResult,
  ScopedPrompt,
  RestrictBlocker,
  ToolDescriptor,
  ViewFieldOverride,
  ViewLayoutConfig,
  ViewQueryConfig,
  WorkspaceInfo,
  WorkforceStatusResult,
  AgentProductivityResult,
  WorkspaceOverview,
  WorkspaceProductivityResult,
  CacheHealthResult,
  NeedsAttentionResult,
  WorkspaceActivityEvent,
  WorkspaceTasksResult,
  TaskThreadResult,
  TaskLaunchLinks,
  AnchorStreamResult,
  AgentRunResult,
} from "./types";
// №16 D-WF13 — legacy workflow client types + methods retired.

export interface McpClientConfig {
  endpoint: string;
}

let requestCounter = 0;
function nextRequestId(): string {
  requestCounter += 1;
  return `c${Date.now().toString(36)}-${requestCounter}`;
}

export class McpError extends Error {
  code: number;
  data?: unknown;
  constructor(message: string, code: number, data?: unknown) {
    super(message);
    this.name = "McpError";
    this.code = code;
    this.data = data;
  }
}

/** Trigger a browser "save as" for a blob under the given filename. */
function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

export class McpClient {
  private readonly config: McpClientConfig;
  constructor(config: McpClientConfig) {
    this.config = config;
  }

  private buildHeaders(): HeadersInit {
    const headers: Record<string, string> = {
      "content-type": "application/json",
    };
    const token = getAccessToken();
    if (token) {
      headers["authorization"] = `Bearer ${token}`;
    }
    return headers;
  }

  /** Origin of the API worker (e.g. https://dev.kuvio.ai) — for non-JSON-RPC routes like OAuth. */
  get apiOrigin(): string {
    return new URL(this.config.endpoint).origin;
  }

  /**
   * Sprint 16b — mint the provider authorize URL for a wrapped API's OAuth flow.
   * Authenticated (Bearer) so the worker can bind the flow to the current user;
   * chrome then opens the returned URL in a popup.
   */
  async getWrappedApiAuthorizeUrl(slug: string): Promise<string> {
    const resp = await fetch(
      `${this.apiOrigin}/oauth/wrapped/${encodeURIComponent(slug)}/authorize?json=1`,
      { headers: this.buildHeaders() },
    );
    if (!resp.ok) throw new Error(`Couldn't start authorization (HTTP ${resp.status})`);
    const data = (await resp.json()) as { authorize_url?: string; error?: string };
    if (!data.authorize_url) throw new Error(data.error ?? "No authorize URL returned");
    return data.authorize_url;
  }

  /**
   * Issue a single JSON-RPC call. Throws McpError on a JSON-RPC error
   * response. Returns the unwrapped `result`.
   */
  async call<T>(method: string, params?: Record<string, unknown>): Promise<T> {
    const req: JsonRpcRequest = {
      jsonrpc: "2.0",
      id: nextRequestId(),
      method,
      ...(params ? { params } : {}),
    };
    const response = await fetch(this.config.endpoint, {
      method: "POST",
      headers: this.buildHeaders(),
      body: JSON.stringify(req),
    });
    if (!response.ok && response.status !== 400 && response.status !== 409) {
      throw new McpError(
        `Transport error: HTTP ${response.status}`,
        -32603,
      );
    }
    const text = await response.text();
    // The server returns text/event-stream for schema-mutating tool calls
    // (so it can attach list_changed notifications). Parse SSE here so the
    // caller doesn't need to think about it.
    const body = parseSseOrJson(text);
    const envelope = (Array.isArray(body) ? body[0] : body) as JsonRpcResponse<T>;
    if (envelope?.error) {
      throw new McpError(envelope.error.message, envelope.error.code, envelope.error.data);
    }
    return envelope.result as T;
  }

  /**
   * Batched JSON-RPC. Each item is a {method, params} pair. Returns an
   * array of results in the same order. Errors in individual items are
   * surfaced as `McpError | T` so the caller can handle them per slot.
   */
  async batch<T>(
    items: { method: string; params?: Record<string, unknown> }[],
  ): Promise<(T | McpError)[]> {
    if (items.length === 0) return [];
    const reqs: JsonRpcRequest[] = items.map((it) => ({
      jsonrpc: "2.0",
      id: nextRequestId(),
      method: it.method,
      ...(it.params ? { params: it.params } : {}),
    }));
    const response = await fetch(this.config.endpoint, {
      method: "POST",
      headers: this.buildHeaders(),
      body: JSON.stringify(reqs),
    });
    const text = await response.text();
    const body = parseSseOrJson(text) as JsonRpcResponse<T>[];
    // Re-order by id matching to be defensive — though the spec says order is preserved.
    const byId = new Map(body.map((r) => [String(r.id), r]));
    return reqs.map((req) => {
      const env = byId.get(String(req.id));
      if (!env) return new McpError("missing_response", -32603);
      if (env.error) return new McpError(env.error.message, env.error.code, env.error.data);
      return env.result as T;
    });
  }

  // -------------------------------------------------------------------------
  // High-level convenience wrappers
  // -------------------------------------------------------------------------

  async toolsList(): Promise<ToolDescriptor[]> {
    const result = await this.call<{ tools: ToolDescriptor[] }>("tools/list");
    return result.tools;
  }

  async toolsCall<T>(name: string, args: Record<string, unknown> = {}): Promise<McpToolResult<T>> {
    const scope = getCurrentScope(getCurrentWorkspaceId());
    const injected = scope.length > 0 ? { ...args, _scope: scope } : args;
    return this.call<McpToolResult<T>>("tools/call", { name, arguments: injected });
  }

  async getWorkspaceInfo(): Promise<WorkspaceInfo> {
    const result = await this.toolsCall<WorkspaceInfo>("get_workspace_info");
    return result.structuredContent as WorkspaceInfo;
  }

  // Sprint 23 — Workforce status (polled by the Workforce route).
  async getWorkforceStatus(agentIds?: string[]): Promise<WorkforceStatusResult> {
    const args: Record<string, unknown> = {};
    if (agentIds && agentIds.length > 0) args.agent_ids = agentIds;
    const result = await this.toolsCall<WorkforceStatusResult>("get_workforce_status", args);
    if (result.isError) throw toMutationError(result);
    return (
      (result.structuredContent as WorkforceStatusResult) ?? { statuses: [], generated_at: "" }
    );
  }

  /**
   * №53 · 53.4 — Handled, the receipt. One call for the whole page: the bands, the
   * fold counts and the totals are computed together server-side, so the surface
   * can never render a fold count that disagrees with the band it sits in.
   */
  async getHandledDay(opts?: { date?: string; limit?: number }): Promise<HandledDay> {
    const args: Record<string, unknown> = {};
    if (opts?.date) args.date = opts.date;
    if (typeof opts?.limit === "number") args.limit = opts.limit;
    const result = await this.toolsCall<HandledDay>("get_handled_day", args);
    if (result.isError) throw toMutationError(result);
    return (
      (result.structuredContent as HandledDay) ?? {
        date: "", bands: [], own_time: [],
        totals: { lines: 0, consequential: 0, folded: 0, refused: 0, hires: 0 },
        axis_note: "",
      }
    );
  }

  // Sprint 23.6 — per-agent productivity scorecard (called from the profile hero).
  async getAgentProductivity(
    agentId: string,
    opts?: { since?: string; compareSince?: string },
  ): Promise<AgentProductivityResult> {
    const args: Record<string, unknown> = { agent_id: agentId };
    if (opts?.since) args.since = opts.since;
    if (opts?.compareSince) args.compare_since = opts.compareSince;
    const result = await this.toolsCall<AgentProductivityResult>(
      "get_agent_productivity",
      args,
    );
    if (result.isError) throw toMutationError(result);
    return result.structuredContent as AgentProductivityResult;
  }

  // №16 D-WF13 — the legacy workflow client methods (list/get/start/
  // transition/cancel definitions + instances + eligibility equip/unequip) are retired.

  // Sprint 23.8 — workspace home aggregations (the `/home` daily briefing).
  async getWorkspaceOverview(): Promise<WorkspaceOverview> {
    const result = await this.toolsCall<WorkspaceOverview>("get_workspace_overview");
    if (result.isError) throw toMutationError(result);
    return result.structuredContent as WorkspaceOverview;
  }

  async getWorkspaceProductivity(opts?: {
    since?: string;
    compareSince?: string;
  }): Promise<WorkspaceProductivityResult> {
    const args: Record<string, unknown> = {};
    if (opts?.since) args.since = opts.since;
    if (opts?.compareSince) args.compare_since = opts.compareSince;
    const result = await this.toolsCall<WorkspaceProductivityResult>(
      "get_workspace_productivity",
      args,
    );
    if (result.isError) throw toMutationError(result);
    return result.structuredContent as WorkspaceProductivityResult;
  }

  // Sprint 24.13 — prompt-cache health (owner-only; backs /admin/cache-health).
  async getCacheHealth(days?: number): Promise<CacheHealthResult> {
    const args: Record<string, unknown> = {};
    if (typeof days === "number") args.days = days;
    const result = await this.toolsCall<CacheHealthResult>("get_cache_health", args);
    if (result.isError) throw toMutationError(result);
    return result.structuredContent as CacheHealthResult;
  }

  async listWorkspaceNeedsAttention(): Promise<NeedsAttentionResult> {
    const result = await this.toolsCall<NeedsAttentionResult>(
      "list_workspace_needs_attention",
    );
    if (result.isError) throw toMutationError(result);
    return (result.structuredContent as NeedsAttentionResult) ?? { items: [], count: 0 };
  }

  async listWorkspaceActivity(limit?: number): Promise<WorkspaceActivityEvent[]> {
    const args: Record<string, unknown> = {};
    if (typeof limit === "number") args.limit = limit;
    const result = await this.toolsCall<{ events: WorkspaceActivityEvent[] }>(
      "list_workspace_activity",
      args,
    );
    if (result.isError) throw toMutationError(result);
    return result.structuredContent?.events ?? [];
  }

  async listWorkspaceTasks(opts?: {
    limit?: number;
    includeDone?: boolean;
  }): Promise<WorkspaceTasksResult> {
    const args: Record<string, unknown> = {};
    if (typeof opts?.limit === "number") args.limit = opts.limit;
    if (opts?.includeDone) args.include_done = true;
    const result = await this.toolsCall<WorkspaceTasksResult>(
      "list_workspace_tasks",
      args,
    );
    if (result.isError) throw toMutationError(result);
    return (
      (result.structuredContent as WorkspaceTasksResult) ?? {
        tasks: [],
        active_count: 0,
      }
    );
  }

  // Sprint 23 — workspace_context k/v read/write (used for the language_theme key).
  async getWorkspaceContext(key: string): Promise<string | null> {
    const result = await this.toolsCall<{ entry?: { value?: string } | null }>(
      "get_workspace_context",
      { key },
    );
    if (result.isError) throw toMutationError(result);
    const sc = result.structuredContent as { entry?: { value?: string } | null } | undefined;
    return sc?.entry?.value ?? null;
  }

  async setWorkspaceContext(key: string, value: string): Promise<void> {
    const result = await this.toolsCall("set_workspace_context", { key, value });
    if (result.isError) throw toMutationError(result);
  }

  async listEntityTypes(): Promise<EntityTypeDefinition[]> {
    const result = await this.toolsCall<{ entity_types: EntityTypeDefinition[] }>(
      "list_entity_types",
    );
    return (result.structuredContent?.entity_types ?? []) as EntityTypeDefinition[];
  }

  /**
   * Sprint 14f (Layer 5) — re-synthesise the workspace's tool list server-side and
   * return the current counts. Backs the Cmd-K "Refresh tools" command. Note: this
   * is a server-side re-synthesis confirming the live tool surface; it does not push
   * a refresh into a separate AI host's cached session (each host refreshes its own
   * tools/list on `notifications/tools/list_changed` or its next `refresh_tools` call).
   */
  async refreshTools(): Promise<{
    entity_types: string[];
    static_tool_count: number;
    entity_tool_count: number;
  }> {
    const result = await this.toolsCall<{
      entity_types: string[];
      static_tool_count: number;
      entity_tool_count: number;
    }>("refresh_tools");
    const sc = result.structuredContent;
    return {
      entity_types: sc?.entity_types ?? [],
      static_tool_count: sc?.static_tool_count ?? 0,
      entity_tool_count: sc?.entity_tool_count ?? 0,
    };
  }

  // Entity operations are dispatched against the synthesised per-type tools
  // (create_<slug> / list_<plural> / get_<slug> / update_<slug> / archive_<slug>).

  async listEntities(
    toolName: string,
    filters?: ListFilters,
  ): Promise<ListEntitiesResult> {
    const args: Record<string, unknown> = {};
    if (filters) {
      for (const [key, value] of Object.entries(filters)) {
        if (value === undefined || value === null || value === "") continue;
        args[key] = value;
      }
    }
    // Sprint 24.9 — the worker now projects list_* rows to a minimal default set
    // for external hosts. The console renders full table grids, so it opts into
    // every column (and out of the 10KB size cap) via the `["*"]` escape hatch.
    if (!("fields" in args)) args.fields = ["*"];
    const result = await this.toolsCall<ListEntitiesResult>(toolName, args);
    if (result.isError) throw toMutationError(result);
    return (result.structuredContent ?? { entities: [], count: 0 }) as ListEntitiesResult;
  }

  async getEntity(toolName: string, id: string): Promise<EntityRecord> {
    const result = await this.toolsCall<EntityRecord>(toolName, { id });
    if (result.isError) throw toMutationError(result);
    return result.structuredContent as EntityRecord;
  }

  async createEntity(toolName: string, fields: Record<string, unknown>): Promise<EntityRecord> {
    const result = await this.toolsCall<EntityRecord>(toolName, fields);
    if (result.isError) throw toMutationError(result);
    return result.structuredContent as EntityRecord;
  }

  async updateEntity(
    toolName: string,
    id: string,
    fields: Record<string, unknown>,
    expectedVersion?: number,
  ): Promise<EntityRecord> {
    const args: Record<string, unknown> = { id, ...fields };
    if (expectedVersion !== undefined) args.expected_version = expectedVersion;
    const result = await this.toolsCall<EntityRecord>(toolName, args);
    if (result.isError) throw toMutationError(result);
    return result.structuredContent as EntityRecord;
  }

  // -------------------------------------------------------------------------
  // Library operations (Sprint 3.0/3.1)
  // -------------------------------------------------------------------------

  async getNode(id: string): Promise<LibraryNodeDetail> {
    const result = await this.toolsCall<{ node: LibraryNodeDetail }>("get_node", { id });
    if (result.isError) throw toMutationError(result);
    const payload = result.structuredContent as { node: LibraryNodeDetail };
    return payload.node;
  }

  async findRelated(id: string): Promise<FindRelatedResult> {
    const result = await this.toolsCall<FindRelatedResult>("find_related", { id });
    if (result.isError) throw toMutationError(result);
    return (result.structuredContent as FindRelatedResult) ?? { id, name: "", type: "", relationships: {} };
  }

  /** Sprint 12 — full detail for one entity row (fields, transitions, owned content, refs). */
  async getRowDetail(entityTypeSlug: string, entityId: string): Promise<RowDetailResult> {
    const result = await this.toolsCall<RowDetailResult>("get_row_detail", {
      entity_type_slug: entityTypeSlug,
      entity_id: entityId,
    });
    if (result.isError) throw toMutationError(result);
    return result.structuredContent as RowDetailResult;
  }

  async listTree(params: {
    parent_id?: string | null;
    depth?: number;
    type?: string;
    include_archived?: boolean;
  } = {}): Promise<LibraryNode[]> {
    const args: Record<string, unknown> = {};
    if (params.parent_id !== undefined) args.parent_id = params.parent_id;
    if (params.depth !== undefined) args.depth = params.depth;
    if (params.type !== undefined) args.type = params.type;
    if (params.include_archived) args.include_archived = true;
    const result = await this.toolsCall<{ nodes: LibraryNode[] }>("list_tree", args);
    if (result.isError) throw toMutationError(result);
    const payload = result.structuredContent as { nodes?: LibraryNode[] };
    return payload?.nodes ?? [];
  }

  async createTopic(params: {
    name: string;
    parent_id?: string;
    instructions?: string;
    document?: string;
  }): Promise<LibraryNode> {
    const result = await this.toolsCall<{ node: LibraryNode }>("create_topic", params as Record<string, unknown>);
    if (result.isError) throw toMutationError(result);
    const payload = result.structuredContent as { node: LibraryNode };
    return payload.node;
  }

  /** Sprint 5o — create a page node. `body` may include YAML frontmatter. */
  async createPage(params: {
    name: string;
    parent_id?: string;
    body?: string;
    surface?: string;
    status?: string;
    frontmatter?: Record<string, unknown>;
  }): Promise<LibraryNode> {
    const result = await this.toolsCall<{ node: LibraryNode }>("create_page", params as Record<string, unknown>);
    if (result.isError) throw toMutationError(result);
    const payload = result.structuredContent as { node: LibraryNode };
    return payload.node;
  }

  /** Sprint 5o — partial update of a page node. */
  async updatePage(params: {
    id: string;
    name?: string;
    body?: string;
    surface?: string;
    status?: string;
    frontmatter?: Record<string, unknown> | null;
  }): Promise<LibraryNode> {
    const result = await this.toolsCall<{ node: LibraryNode }>("update_page", params as Record<string, unknown>);
    if (result.isError) throw toMutationError(result);
    const payload = result.structuredContent as { node: LibraryNode };
    return payload.node;
  }

  async updateNode(params: {
    id: string;
    name?: string;
    description?: string;
    document?: string;
    instructions?: string;
    surface?: string;
    status?: string;
    owner?: string;
    sort_order?: number;
    agent_config?: AgentConfig | null;
  }): Promise<LibraryNode> {
    const result = await this.toolsCall<{ node: LibraryNode }>("update_node", params as Record<string, unknown>);
    if (result.isError) throw toMutationError(result);
    const payload = result.structuredContent as { node: LibraryNode };
    return payload.node;
  }

  async updateWorkspaceName(name: string): Promise<{ ok: boolean; name: string }> {
    const result = await this.toolsCall<{ ok: boolean; name: string }>("update_workspace_name", { name });
    if (result.isError) throw toMutationError(result);
    return result.structuredContent as { ok: boolean; name: string };
  }

  async setWorkspaceInstructions(
    instructions: string,
  ): Promise<{ ok: boolean; instructions: string }> {
    const result = await this.toolsCall<{ ok: boolean; instructions: string }>(
      "set_workspace_instructions",
      { instructions },
    );
    if (result.isError) throw toMutationError(result);
    return result.structuredContent as { ok: boolean; instructions: string };
  }

  async archiveNodeCall(id: string, cascade?: boolean): Promise<void> {
    const args: Record<string, unknown> = { id };
    if (cascade !== undefined) args.cascade = cascade;
    const result = await this.toolsCall("archive_node", args);
    if (result.isError) throw toMutationError(result);
  }

  async moveNode(params: {
    id: string;
    new_parent_id: string | null;
    sort_order?: number;
  }): Promise<LibraryNode> {
    const result = await this.toolsCall<{ node: LibraryNode }>("move_node", params as Record<string, unknown>);
    if (result.isError) throw toMutationError(result);
    const payload = result.structuredContent as { node: LibraryNode };
    return payload.node;
  }

  async promoteRow(entityId: string): Promise<{ node_id: string }> {
    const result = await this.toolsCall<{ node_id: string }>("promote_row", { entity_id: entityId });
    if (result.isError) throw toMutationError(result);
    return result.structuredContent as { node_id: string };
  }

  async demoteRow(entityId: string): Promise<void> {
    const result = await this.toolsCall("demote_row", { entity_id: entityId });
    if (result.isError) throw toMutationError(result);
  }

  async createFolder(params: {
    name: string;
    description?: string;
    parent_id?: string;
    parent_path?: string;
  }): Promise<LibraryNode> {
    const result = await this.toolsCall<{ node: LibraryNode }>("create_folder", params as Record<string, unknown>);
    if (result.isError) throw toMutationError(result);
    const payload = result.structuredContent as { node: LibraryNode };
    return payload.node;
  }

  async listActivity(params: {
    agentId?: string;
    surface?: string;
    since?: string;
    limit?: number;
    cursor?: string;
  } = {}): Promise<ListActivityResult> {
    const args: Record<string, unknown> = {};
    if (params.agentId) args.agent_id = params.agentId;
    if (params.surface) args.surface = params.surface;
    if (params.since) args.since = params.since;
    if (params.limit !== undefined) args.limit = params.limit;
    if (params.cursor) args.cursor = params.cursor;
    const result = await this.toolsCall<ListActivityResult>("list_activity", args);
    if (result.isError) throw toMutationError(result);
    return (result.structuredContent ?? { items: [], next_cursor: null }) as ListActivityResult;
  }

  async listChildren(parentId: string, type?: string): Promise<ListChildrenResult[]> {
    const args: Record<string, unknown> = { parent_id: parentId };
    if (type) args.type = type;
    const result = await this.toolsCall<{ children: ListChildrenResult[] }>("list_children", args);
    if (result.isError) throw toMutationError(result);
    const payload = result.structuredContent as { children: ListChildrenResult[] };
    return payload.children ?? [];
  }

  // -------------------------------------------------------------------------
  // Files (Sprint 5n) — list_files / upload_file / get_file
  // -------------------------------------------------------------------------

  async listFiles(params: {
    entity_ref?: string;
    mime_type?: string;
    limit?: number;
    offset?: number;
    include_archived?: boolean;
  } = {}): Promise<ListFilesResult> {
    const args: Record<string, unknown> = {};
    if (params.entity_ref) args.entity_ref = params.entity_ref;
    if (params.mime_type) args.mime_type = params.mime_type;
    if (params.limit !== undefined) args.limit = params.limit;
    if (params.offset !== undefined) args.offset = params.offset;
    if (params.include_archived) args.include_archived = true;
    const result = await this.toolsCall<ListFilesResult>("list_files", args);
    if (result.isError) throw toMutationError(result);
    return (result.structuredContent ?? { files: [], count: 0, limit: 0, offset: 0 }) as ListFilesResult;
  }

  async uploadFile(params: {
    filename: string;
    content: string;
    mime_type?: string;
    encoding?: "utf-8" | "base64";
    entity_ref?: string | null;
    description?: string | null;
  }): Promise<FileMetadata> {
    // The upload_file tool returns `{ file }` on success (no `ok` flag); errors
    // surface as isError with `{ error, message }`. Throw the friendly message.
    const result = await this.toolsCall<{ file?: FileMetadata; error?: string; message?: string }>(
      "upload_file",
      params as Record<string, unknown>,
    );
    if (result.isError || !result.structuredContent?.file) {
      const data = (result.structuredContent ?? {}) as { error?: string; message?: string };
      throw new McpError(data.message || data.error || "upload_failed", -32000, data);
    }
    return result.structuredContent.file;
  }

  async getFile(id: string, mode?: "auto" | "url" | "inline"): Promise<GetFileResult> {
    const args: Record<string, unknown> = { id };
    if (mode) args.mode = mode;
    const result = await this.toolsCall<GetFileResult>("get_file", args);
    if (result.isError) throw toMutationError(result);
    return result.structuredContent as GetFileResult;
  }

  /**
   * §10.4 follow-on — download a closed period's sealed statutory pack as one
   * ZIP. Authenticated fetch (Bearer header) → blob → browser save, so the
   * download is secure without a token-in-URL and works under bearer auth
   * (a plain window.open would not carry the header). Read-only: the route
   * never reopens the period.
   */
  async downloadPeriodPack(ledgerId: string, period: string): Promise<void> {
    const resp = await fetch(
      `${this.apiOrigin}/ledger/${encodeURIComponent(ledgerId)}/periods/${encodeURIComponent(period)}/pack.zip`,
      { headers: this.buildHeaders() },
    );
    if (!resp.ok) throw new McpError(`Couldn't download the pack (HTTP ${resp.status})`, -32000);
    saveBlob(await resp.blob(), `trust-pack-${period}.zip`);
  }

  /**
   * §10.5 — securely download one file (e.g. a sealed period artifact) to disk.
   * get_file returns either inline content or a bearer-only `/files/:id/download`
   * URL; a plain window.open on that URL 401s on deployed workers (the session
   * bearer isn't a cookie). So we fetch WITH the Bearer header → blob → save,
   * mirroring downloadPeriodPack. Returns the saved filename.
   */
  async downloadFile(fileId: string): Promise<string> {
    const meta = await this.getFile(fileId, "auto");
    if (!meta.ok) throw new McpError(`File ${fileId} not found`, -32000);
    let blob: Blob;
    if (meta.delivery === "inline") {
      const bytes = meta.encoding === "base64"
        ? Uint8Array.from(atob(meta.content), (c) => c.charCodeAt(0))
        : new TextEncoder().encode(meta.content);
      blob = new Blob([bytes as BlobPart], { type: meta.file.mime_type });
    } else {
      const resp = await fetch(meta.url, { headers: this.buildHeaders() });
      if (!resp.ok) throw new McpError(`Couldn't download ${meta.file.filename} (HTTP ${resp.status})`, -32000);
      blob = await resp.blob();
    }
    saveBlob(blob, meta.file.filename);
    return meta.file.filename;
  }

  // -------------------------------------------------------------------------
  // Sprint 26.0 — Tasks: the one work noun. Two lenses (queue | plan); current =
  // rank #1 (lowest sort_order); references link to pages/entities; sub-tasks via
  // parent_task_id. No focus, no cadence.
  // -------------------------------------------------------------------------

  /** List an agent's tasks (queue lens by default — flat, ranked, #1 = current). */
  async listTasks(
    agentId: string,
    opts: {
      lens?: "queue" | "plan";
      status?: TaskStatus;
      include_inbox?: boolean;
      include_done?: boolean;
      include_dropped?: boolean;
      include_archived?: boolean;
    } = {},
  ): Promise<Task[]> {
    const args: Record<string, unknown> = { agent_id: agentId };
    if (opts.lens) args.lens = opts.lens;
    if (opts.status) args.status = opts.status;
    if (opts.include_inbox) args.include_inbox = true;
    if (opts.include_done) args.include_done = true;
    if (opts.include_dropped) args.include_dropped = true;
    if (opts.include_archived) args.include_archived = true;
    const result = await this.toolsCall<{ tasks: Task[] }>("list_tasks", args);
    if (result.isError) throw toMutationError(result);
    return (result.structuredContent as { tasks?: Task[] })?.tasks ?? [];
  }

  /** Plan lens — the nested sub-task tree (each task carries `children`). */
  async listTaskPlan(agentId: string, opts: { status?: TaskStatus; include_archived?: boolean } = {}): Promise<Task[]> {
    return this.listTasks(agentId, { ...opts, lens: "plan" });
  }

  /**
   * №22.6 (H2 / O1) — the answered ledger. Filter per matter, per instance, per
   * hire, or by period; newest first.
   */
  async listAnsweredDecisions(q: {
    anchor_type?: string; anchor_no?: string; instance_no?: number;
    agent_id?: string; task_id?: string; since?: string; limit?: number;
  } = {}): Promise<AnsweredDecision[]> {
    const result = await this.toolsCall<{ answers: AnsweredDecision[] }>("list_answered_decisions", q);
    if (result.isError) throw toMutationError(result);
    return (result.structuredContent as { answers?: AnsweredDecision[] })?.answers ?? [];
  }

  /**
   * YOUR Needs-You cards (№22.6 D-NY1). Returns the ENVELOPE, not a bare list:
   * the server reports what its scope withheld, and dropping that here would
   * repeat this lane's own defect one layer up — a fact served and thrown away.
   * `agent_id` reads ONE hire's desk, which is a place and so is not filtered.
   */
  async listNeedsYou(agentId?: string): Promise<NeedsYouFeed> {
    const args: Record<string, unknown> = {};
    if (agentId) args.agent_id = agentId;
    const result = await this.toolsCall<{ tasks: Task[] }>("list_needs_you", args);
    if (result.isError) throw toMutationError(result);
    const sc = result.structuredContent as { tasks?: Task[]; read?: string; scope?: NeedsYouFeed["scope"] } | undefined;
    return {
      tasks: sc?.tasks ?? [],
      read: sc?.read === "place" ? "place" : "home",
      // A worker older than this deploy sends no `scope`; report nothing withheld
      // rather than inventing a count. The caption then simply omits the clause.
      scope: sc?.scope ?? { shown: sc?.tasks?.length ?? 0, filtered: 0, byReason: {} },
    };
  }

  /** The brain snapshot: one composite read backing the whole agent page. */
  async getAgentBrainSnapshot(agentId: string): Promise<AgentBrainSnapshot> {
    // The server tool is `get_agent_snapshot` (Sprint 27.0 renamed it from
    // `get_agent_brain_snapshot`; the console kept the old name, so this call hit a
    // non-existent tool and the agent desk silently rendered empty — the RC1-2
    // symptom). Call the real name.
    const result = await this.toolsCall<{ snapshot: AgentBrainSnapshot }>("get_agent_snapshot", {
      agent_id: agentId,
    });
    if (result.isError) throw toMutationError(result);
    return (result.structuredContent as { snapshot: AgentBrainSnapshot }).snapshot;
  }

  /** Composite single-task read backing the body-first detail surface. */
  async getTask(taskId: string): Promise<TaskDetailResult> {
    const result = await this.toolsCall<TaskDetailResult>("get_task", { task_id: taskId });
    if (result.isError) throw toMutationError(result);
    return result.structuredContent as TaskDetailResult;
  }

  // ---- Spec №22: the collaboration loop --------------------------------------

  /** D-CL1 — the task's execution thread: the bound conversation + its stamped runs. */
  async getTaskThread(taskId: string): Promise<TaskThreadResult> {
    const result = await this.toolsCall<TaskThreadResult>("get_task_thread", { task_id: taskId });
    if (result.isError) throw toMutationError(result);
    return (result.structuredContent as TaskThreadResult) ?? { conversation: null, runs: [] };
  }

  /** №22.3 (D-CV9/11/12) — the anchor's stream: runs + open cards + activities under
   *  the anchor, time-ordered newest-first. The five rollups via `anchorKind`. */
  async getAnchorStream(anchorKind: string, anchorId: string | undefined, limit?: number): Promise<AnchorStreamResult> {
    const result = await this.toolsCall<AnchorStreamResult>("get_anchor_stream", {
      anchor_kind: anchorKind,
      ...(anchorId ? { anchor_id: anchorId } : {}),
      ...(limit ? { limit } : {}),
    });
    if (result.isError) throw toMutationError(result);
    return (result.structuredContent as AnchorStreamResult) ?? { anchor: { kind: anchorKind as never, id: anchorId ?? "" }, events: [] };
  }

  /** D-CL5 — deep links to continue the task in Claude / ChatGPT. */
  async getTaskLaunchLinks(taskId: string): Promise<TaskLaunchLinks> {
    const result = await this.toolsCall<TaskLaunchLinks>("get_task_launch_links", { task_id: taskId });
    if (result.isError) throw toMutationError(result);
    return result.structuredContent as TaskLaunchLinks;
  }

  /** D-CL2 — the human takes the task over from its agent (the agent yields). */
  async takeOverTask(taskId: string): Promise<void> {
    const result = await this.toolsCall("take_over_task", { task_id: taskId });
    if (result.isError) throw toMutationError(result);
  }

  /**
   * D-CC2 — answer a decision card by choosing one of its prepared options. Executes
   * THAT option's snapshotted verb through the one dispatch (stamped requested-by-you),
   * records the answer in the thread, and clears the card on every surface (D-CC4).
   */
  async answerNeedsYou(taskId: string, optionIndex: number): Promise<{
    answered: boolean; option_label: string; executed_tool: string | null;
    /** №22.6.1 (D-NY6, work item A/B) — present only once the batch is exhausted. */
    resume?: NeedsYouResumeResult;
  }> {
    const result = await this.toolsCall("answer_needs_you", { task_id: taskId, option_index: optionIndex });
    if (result.isError) throw toMutationError(result);
    return result.structuredContent as { answered: boolean; option_label: string; executed_tool: string | null; resume?: NeedsYouResumeResult };
  }

  /**
   * №22.5 (C4) — skip the decision card standing on a task. Never a silent dismissal:
   * the server resolves the card with a ⊘ receipt, writes the stated consequence into
   * the thread, and leaves a mark on the task. A batched card advances to its next
   * question rather than abandoning the rest.
   */
  async skipNeedsYou(taskId: string): Promise<{
    outcome: string; consequence: string; next?: { index: number; total: number };
    /** №22.6.1 (D-NY6, work item A/B) — present only once the batch is exhausted. */
    resume?: NeedsYouResumeResult;
  }> {
    const result = await this.toolsCall("skip_needs_you", { task_id: taskId });
    if (result.isError) throw toMutationError(result);
    return result.structuredContent as { outcome: string; consequence: string; next?: { index: number; total: number }; resume?: NeedsYouResumeResult };
  }

  /**
   * D-CL3 — the play button. Runs the agent on the task under its own scope, bound to
   * the task thread (venue = native, the in-app cheap tier). `input` seeds the turn:
   * the task brief on the first play, the human's message when assisting/interjecting.
   * Blocking (the run completes, then the caller refetches the thread).
   */
  async playTaskRun(agentId: string, params: { task_id: string; input: string; requested_by_name?: string }): Promise<AgentRunResult> {
    const resp = await fetch(`${this.apiOrigin}/ai/agent/${encodeURIComponent(agentId)}/run`, {
      method: "POST",
      headers: this.buildHeaders(),
      body: JSON.stringify({
        input: params.input,
        task_id: params.task_id,
        venue: "native",
        ...(params.requested_by_name ? { requested_by_name: params.requested_by_name } : {}),
      }),
    });
    if (!resp.ok) {
      const text = await resp.text().catch(() => "");
      throw new Error(`Run failed (HTTP ${resp.status})${text ? `: ${text}` : ""}`);
    }
    return (await resp.json()) as AgentRunResult;
  }

  /** Create a task. The content IS the task — pass it as `body`. */
  async createTask(params: {
    agent_id: string;
    title: string;
    body?: string;
    instructions?: string;
    status?: "inbox" | "queued";
    parent_task_id?: string;
    due_by?: string;
    workflow_instance_id?: string;
    references?: Array<{ target_kind: TaskReferenceKind; target_id: string }>;
  }): Promise<{ task: Task; warning: string | null }> {
    const args: Record<string, unknown> = { agent_id: params.agent_id, title: params.title };
    if (params.body !== undefined) args.body = params.body;
    if (params.instructions) args.instructions = params.instructions;
    if (params.status) args.status = params.status;
    if (params.parent_task_id) args.parent_task_id = params.parent_task_id;
    if (params.due_by) args.due_by = params.due_by;
    if (params.workflow_instance_id) args.workflow_instance_id = params.workflow_instance_id;
    if (params.references?.length) args.references = params.references;
    const result = await this.toolsCall<{ task: Task; warning: string | null }>("create_task", args);
    if (result.isError) throw toMutationError(result);
    const data = result.structuredContent as { task: Task; warning?: string | null };
    return { task: data.task, warning: data.warning ?? null };
  }

  async updateTask(agentId: string, taskId: string, fields: Record<string, unknown>): Promise<Task> {
    const result = await this.toolsCall<{ task: Task }>("update_task", {
      agent_id: agentId,
      task_id: taskId,
      ...fields,
    });
    if (result.isError) throw toMutationError(result);
    return (result.structuredContent as { task: Task }).task;
  }

  /** Re-rank a task. `to_top` makes it current (rank #1 — the only "make current"). */
  async moveTask(params: { task_id: string; to_top?: boolean; to_rank?: number; parent_task_id?: string | null }): Promise<Task> {
    const result = await this.toolsCall<{ task: Task }>("move_task", params as Record<string, unknown>);
    if (result.isError) throw toMutationError(result);
    return (result.structuredContent as { task: Task }).task;
  }

  /** Make a task current — move it to rank #1. */
  async moveTaskToTop(taskId: string): Promise<Task> {
    return this.moveTask({ task_id: taskId, to_top: true });
  }

  async completeTask(agentId: string, taskId: string, summary?: string): Promise<Task> {
    const args: Record<string, unknown> = { agent_id: agentId, task_id: taskId };
    if (summary) args.summary = summary;
    const result = await this.toolsCall<{ task: Task }>("complete_task", args);
    if (result.isError) throw toMutationError(result);
    return (result.structuredContent as { task: Task }).task;
  }

  /** Triage an inbox task into the ranked queue (inbox → queued). */
  async triageTask(taskId: string): Promise<Task> {
    const result = await this.toolsCall<{ task: Task }>("triage_task", { task_id: taskId });
    if (result.isError) throw toMutationError(result);
    return (result.structuredContent as { task: Task }).task;
  }

  async dropTask(taskId: string, reason?: string): Promise<Task> {
    const args: Record<string, unknown> = { task_id: taskId };
    if (reason) args.reason = reason;
    const result = await this.toolsCall<{ task: Task }>("drop_task", args);
    if (result.isError) throw toMutationError(result);
    return (result.structuredContent as { task: Task }).task;
  }

  async archiveTask(agentId: string, taskId: string): Promise<Task> {
    const result = await this.toolsCall<{ task: Task }>("archive_task", { agent_id: agentId, task_id: taskId });
    if (result.isError) throw toMutationError(result);
    return (result.structuredContent as { task: Task }).task;
  }

  async restoreTask(agentId: string, taskId: string): Promise<Task> {
    const result = await this.toolsCall<{ task: Task }>("restore_task", { agent_id: agentId, task_id: taskId });
    if (result.isError) throw toMutationError(result);
    return (result.structuredContent as { task: Task }).task;
  }

  async deleteTask(agentId: string, taskId: string): Promise<void> {
    const result = await this.toolsCall("delete_task", { agent_id: agentId, task_id: taskId });
    if (result.isError) throw toMutationError(result);
  }

  // ---- Task references (links to standalone pages / entities) ----

  async listTaskReferences(taskId: string): Promise<TaskReference[]> {
    const result = await this.toolsCall<{ references: TaskReference[] }>("list_task_references", { task_id: taskId });
    if (result.isError) throw toMutationError(result);
    return (result.structuredContent as { references?: TaskReference[] })?.references ?? [];
  }

  async addTaskReference(taskId: string, targetKind: TaskReferenceKind, targetId: string): Promise<TaskReference[]> {
    const result = await this.toolsCall<{ references: TaskReference[] }>("add_task_reference", {
      task_id: taskId,
      target_kind: targetKind,
      target_id: targetId,
    });
    if (result.isError) throw toMutationError(result);
    return (result.structuredContent as { references?: TaskReference[] })?.references ?? [];
  }

  async removeTaskReference(taskId: string, targetKind: TaskReferenceKind, targetId: string): Promise<TaskReference[]> {
    const result = await this.toolsCall<{ references: TaskReference[] }>("remove_task_reference", {
      task_id: taskId,
      target_kind: targetKind,
      target_id: targetId,
    });
    if (result.isError) throw toMutationError(result);
    return (result.structuredContent as { references?: TaskReference[] })?.references ?? [];
  }

  async listTaskHistory(taskId: string, limit = 10): Promise<RecentEvent[]> {
    const result = await this.toolsCall<{ events: RecentEvent[] }>("list_task_history", { task_id: taskId, limit });
    if (result.isError) throw toMutationError(result);
    return (result.structuredContent as { events?: RecentEvent[] })?.events ?? [];
  }

  /** Compile a routine for host execution (returns its procedure). */
  async runRoutine(routineId: string): Promise<Record<string, unknown>> {
    const result = await this.toolsCall<{ procedure: Record<string, unknown> }>("run_routine", { routine_id: routineId });
    if (result.isError) throw toMutationError(result);
    return (result.structuredContent as { procedure?: Record<string, unknown> })?.procedure ?? {};
  }

  /** Full-text page search — backs the "Attach existing page" picker. */
  async searchPages(query: string, limit = 20): Promise<Array<{ id: string; name: string; surface: string | null; status: string | null; snippet: string }>> {
    const result = await this.toolsCall<{ results?: Array<{ id: string; name: string; surface: string | null; status: string | null; snippet: string }> }>(
      "search_pages",
      { query, limit },
    );
    if (result.isError) throw toMutationError(result);
    return (result.structuredContent as { results?: Array<{ id: string; name: string; surface: string | null; status: string | null; snippet: string }> })?.results ?? [];
  }

  /**
   * Sprint 8: fetch a server-composed MCP prompt (joined message text). Backs
   * "Plan with this agent" — the `plan-with-agent` prompt composes live persona +
   * tasks + activity into a ready-to-paste body.
   */
  async getComposedPrompt(name: string, args: Record<string, string>): Promise<string> {
    const result = await this.call<{
      description?: string;
      messages?: Array<{ content?: { text?: string } }>;
    }>("prompts/get", { name, arguments: args });
    return (result.messages ?? [])
      .map((m) => m.content?.text ?? "")
      .filter(Boolean)
      .join("\n\n");
  }

  // -------------------------------------------------------------------------
  // Sprint 11 — Prompt primitive (type='prompt' library nodes)
  // -------------------------------------------------------------------------

  async createPrompt(params: {
    name: string;
    parent_id?: string;
    description?: string;
    body?: string;
    arguments?: PromptArgDef[];
  }): Promise<LibraryNode> {
    const result = await this.toolsCall<{ node: LibraryNode }>("create_prompt", params as Record<string, unknown>);
    if (result.isError) throw toMutationError(result);
    return (result.structuredContent as { node: LibraryNode }).node;
  }

  async updatePrompt(params: {
    prompt_id: string;
    name?: string;
    description?: string;
    body?: string;
    arguments?: PromptArgDef[];
  }): Promise<LibraryNode> {
    const result = await this.toolsCall<{ node: LibraryNode }>("update_prompt", params as Record<string, unknown>);
    if (result.isError) throw toMutationError(result);
    return (result.structuredContent as { node: LibraryNode }).node;
  }

  async listPromptsInScope(scopeNodeId?: string, includeArchived?: boolean): Promise<ScopedPrompt[]> {
    const result = await this.toolsCall<{ prompts: ScopedPrompt[] }>(
      "list_prompts_in_scope",
      {
        ...(scopeNodeId ? { scope_node_id: scopeNodeId } : {}),
        ...(includeArchived ? { include_archived: true } : {}),
      },
    );
    if (result.isError) throw toMutationError(result);
    return (result.structuredContent as { prompts?: ScopedPrompt[] })?.prompts ?? [];
  }

  // -------------------------------------------------------------------------
  // View operations (Sprint 5.1)
  // -------------------------------------------------------------------------

  async listViews(params?: {
    entity_type?: string;
    view_type?: string;
  }): Promise<LibraryNode[]> {
    const args: Record<string, unknown> = {};
    if (params?.entity_type) args.entity_type = params.entity_type;
    if (params?.view_type) args.view_type = params.view_type;
    const result = await this.toolsCall<{ views: LibraryNode[] }>("list_views", args);
    if (result.isError) throw toMutationError(result);
    const payload = result.structuredContent as { views?: LibraryNode[] };
    return payload?.views ?? [];
  }

  async getView(id: string): Promise<LibraryNodeDetail> {
    const result = await this.toolsCall<{ view: LibraryNodeDetail }>("get_view", { id });
    if (result.isError) throw toMutationError(result);
    const payload = result.structuredContent as { view: LibraryNodeDetail };
    return payload.view;
  }

  async createView(args: {
    library_parent: string;
    name: string;
    view_type: string;
    entity_type: string;
    is_default?: boolean;
    layout: ViewLayoutConfig;
    query?: ViewQueryConfig;
    field_overrides?: ViewFieldOverride[];
  }): Promise<LibraryNode> {
    const result = await this.toolsCall<{ view: LibraryNode }>("create_view", args);
    if (result.isError) throw toMutationError(result);
    const payload = result.structuredContent as { view: LibraryNode };
    return payload.view;
  }

  async updateView(
    id: string,
    args: {
      name?: string;
      is_default?: boolean;
      layout?: ViewLayoutConfig;
      query?: ViewQueryConfig;
      field_overrides?: ViewFieldOverride[];
    },
  ): Promise<LibraryNode> {
    const result = await this.toolsCall<{ view: LibraryNode }>("update_view", { id, ...args });
    if (result.isError) throw toMutationError(result);
    const payload = result.structuredContent as { view: LibraryNode };
    return payload.view;
  }

  async archiveView(id: string): Promise<LibraryNode> {
    const result = await this.toolsCall<{ view: LibraryNode }>("archive_view", { id });
    if (result.isError) throw toMutationError(result);
    const payload = result.structuredContent as { view: LibraryNode };
    return payload.view;
  }

  async createAgent(params: {
    name: string;
    description?: string;
    parent_id?: string;
    instructions?: string;
  }): Promise<LibraryNode> {
    const result = await this.toolsCall<{ node: LibraryNode }>("create_agent", params as Record<string, unknown>);
    if (result.isError) throw toMutationError(result);
    const payload = result.structuredContent as { node: LibraryNode };
    return payload.node;
  }

  async promoteToAgent(
    nodeId: string,
    opts?: { restore_from_reserve?: boolean; instructions?: string; agent_config?: Record<string, unknown> },
  ): Promise<LibraryNode> {
    const result = await this.toolsCall<{ node: LibraryNode }>("promote_to_agent", {
      node_id: nodeId,
      ...(opts?.restore_from_reserve !== undefined ? { restore_from_reserve: opts.restore_from_reserve } : {}),
      ...(opts?.instructions !== undefined ? { instructions: opts.instructions } : {}),
      ...(opts?.agent_config !== undefined ? { agent_config: opts.agent_config } : {}),
    });
    if (result.isError) throw toMutationError(result);
    const payload = result.structuredContent as { node: LibraryNode };
    return payload.node;
  }

  async demoteToFolder(nodeId: string, opts?: { archive_children?: boolean }): Promise<LibraryNode> {
    const result = await this.toolsCall<{ node: LibraryNode }>("demote_to_folder", {
      node_id: nodeId,
      ...(opts?.archive_children !== undefined ? { archive_children: opts.archive_children } : {}),
    });
    if (result.isError) throw toMutationError(result);
    const payload = result.structuredContent as { node: LibraryNode };
    return payload.node;
  }

  async archiveEntity(
    toolName: string,
    id: string,
    expectedVersion?: number,
  ): Promise<ArchiveResult> {
    const args: Record<string, unknown> = { id };
    if (expectedVersion !== undefined) args.expected_version = expectedVersion;
    const result = await this.toolsCall<EntityRecord & { _cascade?: CascadeArchiveResult }>(
      toolName,
      args,
    );
    if (result.isError) throw toMutationError(result);
    const payload = result.structuredContent as EntityRecord & {
      _cascade?: CascadeArchiveResult;
    };
    // Detach _cascade from the entity record so callers see a clean entity
    // shape + a separate cascade summary. The server attaches _cascade at
    // top-level on archive responses; we treat it as response-only metadata.
    const { _cascade, ...entity } = payload;
    return { entity: entity as EntityRecord, cascade: _cascade };
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Parse either an application/json body or a text/event-stream body. */
function parseSseOrJson(text: string): unknown {
  const trimmed = text.trimStart();
  if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
    return JSON.parse(text);
  }
  // SSE: take the FIRST data: line (the response). Subsequent ones are
  // notifications that we don't surface to the caller for now.
  for (const line of text.split("\n")) {
    if (line.startsWith("data: ")) {
      return JSON.parse(line.slice(6));
    }
  }
  throw new McpError("Empty or unparseable response", -32700);
}

/** №13.3 §1 — code → one-line suggestion (mirrors the server's 29.2
 *  ERROR_CODE_SUGGESTIONS, kept tiny here so a failed submit always teaches even
 *  when the raw envelope carries no `suggestion`). */
const VALIDATION_CODE_SUGGESTIONS: Record<string, string> = {
  not_in_choices: "Pick one of the listed values — the value you sent isn't in this field's choices.",
  required: "This field is required. Fill it in and retry.",
  unknown_field: "This field isn't on the type. Remove it (the form derives its fields from the type definition).",
  invalid_email: "Enter a valid email address.",
  invalid_phone: "Enter a valid phone number.",
  invalid_date: "Enter a valid date.",
  wrong_type: "The value's type doesn't match the field. Check the expected shape and retry.",
};

/** №13.3 §1 — turn the per-field validation errors into a single teaching line
 *  ("kind: individual is not one of: person, organisation") instead of the bare
 *  "Validation failed" that hid exactly the info the `individual` bug needed. */
function validationSummary(errors: { field: string; code: string; message: string }[]): string {
  if (errors.length === 0) return "Validation failed.";
  return errors.map((e) => `${e.field}: ${e.message}`).join("  ·  ");
}

/** Map a tool result with `isError: true` into a typed MutationError.
 *  №13.3 §1 — exported so contextual-action forms (VerbLaunchModal) surface the
 *  same teaching error the entity CRUD path throws. */
export function toMutationError(result: McpToolResult): MutationError {
  const data = (result.structuredContent ?? {}) as {
    error?: string | boolean;
    code?: string;
    suggestion?: string;
    errors?: { field: string; code: string; message: string }[];
    current_version?: number;
    expected_version?: number;
    message?: string;
    blockers?: RestrictBlocker[];
  };
  // №13.3 §1 — the 29.2 teaching envelope ({ error:true, code, message, suggestion }).
  // Surface all three verbatim so the form teaches rather than swallowing them.
  if (data.error === true && typeof data.code === "string") {
    return {
      kind: "internal",
      message: data.message ?? data.suggestion ?? "Request failed",
      suggestion: data.suggestion,
      code: data.code,
    };
  }
  if (data.error === "validation_failed") {
    const fieldErrors = data.errors ?? [];
    const firstCode = fieldErrors[0]?.code;
    return {
      kind: "validation",
      // №13.3 §1 — the failing field(s) + why, never a bare "Validation failed".
      message: validationSummary(fieldErrors),
      suggestion: (firstCode && VALIDATION_CODE_SUGGESTIONS[firstCode]) ?? "Check the highlighted field(s) and retry.",
      code: firstCode,
      fieldErrors,
    };
  }
  if (data.error === "version_conflict") {
    return {
      kind: "version_conflict",
      message: `Version conflict — current is v${data.current_version}, you sent v${data.expected_version}`,
      currentVersion: data.current_version,
      expectedVersion: data.expected_version,
    };
  }
  if (data.error === "not_found") {
    return { kind: "not_found", message: "Not found" };
  }
  if (data.error === "restrict_blocked") {
    return {
      kind: "restrict_blocked",
      message: data.message ?? "Cannot archive — there are active children that block it.",
      blockers: data.blockers ?? [],
    };
  }
  if (data.error === "transition_blocked") {
    const blocked = data as unknown as {
      from: string;
      to: string;
      reason: "unknown_current_state" | "transition_not_allowed" | "guards_failed";
      errors: string[];
      allowed_targets?: string[];
    };
    return {
      kind: "transition_blocked",
      message: blocked.errors?.join("; ") ?? "Transition blocked",
      transition: {
        from: blocked.from,
        to: blocked.to,
        reason: blocked.reason,
        ...(blocked.allowed_targets ? { allowedTargets: blocked.allowed_targets } : {}),
      },
    };
  }
  return {
    kind: "internal",
    message: typeof data.error === "string" ? data.error : "Internal error",
  };
}

// ---------------------------------------------------------------------------
// Singleton — configured from Vite env, used app-wide.
// ---------------------------------------------------------------------------

export const mcpClient = new McpClient({
  endpoint: import.meta.env.VITE_MCP_ENDPOINT ?? "https://dev.kuvio.ai/mcp",
});
